export type Rolle = 'ADMIN' | 'USER' | 'VIEWER';
export type Bereich =
  | 'STAMMDATEN'
  | 'ABRECHNUNG'
  | 'AUSWERTUNG'
  | 'KANZLEI'
  | 'COMPLIANCE'
  | 'SYSTEM';

export interface Permissions {
  role: Rolle | undefined;
  /**
   * BL-263: Bereiche, die für diesen Benutzer gesperrt sind — reines
   * Sperr-Array, ergänzend zur Rolle. Leer = keine Einschränkung.
   */
  gesperrteBereiche: Bereich[];
  /** Schreiben allgemein — spiegelt `requireWriteRole` (ADMIN + Buchhalter). */
  canWrite: boolean;
  /** Admin-only: Benutzerverwaltung, Hard-Delete, Settings-Schreiben, Protokoll. */
  isAdmin: boolean;
  /**
   * Consultant-Summary im `/reports`-Tab Personal (Umsatz/Auslastung je Consultant). Die Projekt-Profitabilität
   * ist seit BL-677 (28.10.2026) nur ADMIN und hat keine Oberfläche; echte Gehälter steuert `canSeeGehalt`.
   * Aktuell identisch mit `canWrite`, aber fachlich eine andere Grenze —
   * Entscheidung vom 07.05.2026 („ADMIN + Buchhalter dürfen lesen"). Getrennt
   * gehalten, damit ein späteres Verschärfen auf ADMIN nicht versehentlich
   * alle Schreibrechte mitzieht.
   */
  canSeeSalary: boolean;
  /**
   * Bruttogehalt-Spalte im Personalcontrolling (UI + Excel-Export, BL-236) und Grundgehalt im Consultant-Formular.
   * Seit BL-677 (28.10.2026) NUR ADMIN (Entscheidung des Users), anders als `canSeeSalary` — bewusst ein eigenes Flag:
   * Personalcontrolling selbst (Auslastung/Fakt-Tage/Umsatz) ist seit BL-236
   * für VIEWER lesbar, nur die Gehaltsspalte bleibt gesperrt. Ein Verengen
   * von `canSeeSalary` hätte versehentlich auch die unveränderten
   * `/reports`-Gates mitgezogen, die mit Personalcontrolling nichts zu tun
   * haben.
   */
  canSeeGehalt: boolean;
}

/**
 * Rollen-Ableitung als reine Funktion (12.08.2026), in eigener Datei ohne
 * jeden Import (BL-155, 24.08.2026) — `use-permissions.ts` importiert
 * `useAuth` aus `auth-context.tsx`, das transitiv `@tanstack/react-query`
 * zieht. Vitest/Node stolperte darüber beim reinen Testen dieser Funktion
 * ("Cannot require() ES Module ... in a cycle", @tanstack/react-query
 * mischt ESM/CJS-Exports) — ein reines Modul ohne jeden Import kann diesen
 * Zyklus gar nicht erst auslösen. Gleiches Muster wie `searchFilter` vs.
 * `useSearchFilter` (dort ungefährlich, weil der Hook dort nur `react`
 * importiert, nicht react-query).
 */
export function permissionsForRole(
  role: Rolle | undefined,
  gesperrteBereiche: Bereich[] = [],
): Permissions {
  return {
    role,
    gesperrteBereiche,
    canWrite: role === 'ADMIN' || role === 'USER',
    isAdmin: role === 'ADMIN',
    canSeeSalary: role === 'ADMIN' || role === 'USER',
    canSeeGehalt: role === 'ADMIN', // BL-677 (28.10.2026): Gehälter nur Admin
  };
}

/** BL-263: true, wenn dieser Bereich für den Benutzer gesperrt ist. */
export function istBereichGesperrt(permissions: Permissions, bereich: Bereich): boolean {
  return permissions.gesperrteBereiche.includes(bereich);
}
